import {useCallback, useMemo, useRef} from 'react'
import {StyleSheet, View} from 'react-native'
import {plural} from '@lingui/core/macro'
import {useLingui} from '@lingui/react/macro'

import {gradeLabel} from '#/lib/curriculum'
import {useOpenComposer} from '#/lib/hooks/useOpenComposer'
import {useSetTitle} from '#/lib/hooks/useSetTitle'
import {
  type CommonNavigatorParams,
  type NativeStackScreenProps,
} from '#/lib/routes/types'
import {sanitizeHandle} from '#/lib/strings/handles'
import {
  type Classroom,
  classroomLevelName,
  decodeClassroomInvite,
  defaultPostAudience,
  postVisibility,
  useClassroomActor,
} from '#/state/classrooms'
import {
  ClassroomApiError,
  type ClassroomRoom,
  type ClassroomViewer,
  useAddClassroomContentMutation,
  useClassroomByRouteQuery,
  useClassroomRequestsQuery,
  useRemoveClassroomContentMutation,
} from '#/state/queries/classrooms'
import {type ClassroomPostChoice} from '#/state/shell/composer'
import {PagerWithHeader} from '#/view/com/pager/PagerWithHeader'
import {ErrorScreen} from '#/view/com/util/error/ErrorScreen'
import {FAB} from '#/view/com/util/fab/FAB'
import {type ListRef} from '#/view/com/util/List'
import {atoms as a, useTheme} from '#/alf'
import {useDialogControl} from '#/components/Dialog'
import {EditBig_Stroke2_Corner0_Rounded as EditBigIcon} from '#/components/icons/EditBig'
import * as Layout from '#/components/Layout'
import {Loader} from '#/components/Loader'
import * as Toast from '#/components/Toast'
import {Text} from '#/components/Typography'
import {AddCapsuleDialog} from './components/AddCapsuleDialog'
import {AddFlashbookDialog} from './components/AddFlashbookDialog'
import {AddModuleDialog} from './components/AddModuleDialog'
import {ClassroomBanner} from './components/ClassroomBanner'
import {ClassroomContentSection} from './components/ClassroomContentList'
import {ClassroomHeaderActions} from './components/ClassroomHeaderActions'
import {ClassroomMetrics} from './components/ClassroomMetrics'
import {showClassroomError} from './components/showClassroomError'
import {roomForViewer} from './viewerRoom'

type Props = NativeStackScreenProps<CommonNavigatorParams, 'Classroom'>

interface SectionRef {
  scrollToTop: () => void
}

export function ClassroomScreen({route}: Pick<Props, 'route'>) {
  return (
    <ClassroomScreenInner
      name={route.params.name}
      rkey={route.params.rkey}
      invite={route.params.invite}
    />
  )
}

function ClassroomScreenInner({
  name,
  rkey,
  invite,
}: {
  /** The school's handle, which is the half of the link that names its repo. */
  name: string
  rkey: string
  /** The room's card, when the link that got here carried one. */
  invite?: string
}) {
  const t = useTheme()
  const {t: l} = useLingui()
  /*
   * A link that carried the room's card lends it one thing: the code, which is
   * what lets a private room show itself to somebody who is not in it yet.
   */
  const card = useMemo(() => decodeClassroomInvite(invite), [invite])
  const query = useClassroomByRouteQuery({
    owner: name,
    handle: rkey,
    code: card?.code,
  })
  const room = query.data

  if (room) return <ClassroomPage room={room} inviteCode={card?.code} />

  /*
   * A room that was ended is not drawn as a room.
   *
   * It has its own words rather than the generic failure's, because *this
   * classroom was deleted* is a different answer from *this could not be read*.
   */
  if (
    query.error instanceof ClassroomApiError &&
    query.error.code === 'NotFound'
  ) {
    return (
      <Layout.Screen testID="classroomTakenDown" noInsetTop>
        <Layout.Content>
          <ErrorScreen
            title={l`This classroom was deleted`}
            message={l`It is no longer available, and the work posted in it is gone with it.`}
            showHeader
          />
        </Layout.Content>
      </Layout.Screen>
    )
  }

  if (query.isLoading) {
    return (
      <Layout.Screen testID="classroomReading" noInsetTop>
        <Layout.Content>
          <View style={[a.p_xl, a.gap_md, a.align_center]}>
            <Loader size="lg" />
            <Text
              testID="classroomReadingText"
              style={[a.text_md, t.atoms.text_contrast_medium]}>
              {l`Opening the classroom…`}
            </Text>
          </View>
        </Layout.Content>
      </Layout.Screen>
    )
  }

  return (
    <Layout.Screen testID="classroomNotFound" noInsetTop>
      <Layout.Content>
        <ErrorScreen
          title={l`Classroom not found`}
          /*
            What the reader needs is an answer, not an architecture.

            Someone following a link that does not open is looking at a room
            that is not there, and the two honest reasons are that it was deleted
            or that the link itself is wrong. Nothing is offered that the reader
            could not act on: a code is the way into a classroom that *is* here,
            so a code without its page is an instruction with nothing behind it.
          */
          message={l`This classroom is no longer available. The link may be incorrect, or the classroom may have been deleted.`}
          showHeader
        />
      </Layout.Content>
    </Layout.Screen>
  )
}

/**
 * A classroom, built the way a profile is.
 *
 * The cover, the name, the bio and the counts are the header; below them sit
 * tabs for the kinds of work the room holds, exactly as a profile's tabs hold
 * a person's. What a classroom does *not* have is a face — there is no avatar
 * on the cover — and it has no social counts: nobody follows a classroom, they
 * are in it or they are not.
 */
function ClassroomPage({
  room,
  inviteCode,
}: {
  room: ClassroomRoom
  /** The code the invitation link carried, for the join doors. */
  inviteCode?: string
}) {
  const t = useTheme()
  const {t: l, i18n} = useLingui()
  const actor = useClassroomActor()
  const {viewer} = room
  const canPost = viewer.canPost
  const {openComposer} = useOpenComposer()

  /*
   * The asks waiting at the door, for the seats that may answer them. The
   * server sends a room without them, so they are read on their own.
   */
  const {data: requests} = useClassroomRequestsQuery(
    room.classroom.id,
    viewer.canManage || viewer.role === 'teacher',
  )
  /*
   * Memoized because the dialogs below reset their forms in an effect keyed on
   * the room, and a fresh object every render would wipe what is being typed.
   */
  const classroom = useMemo(
    () => roomForViewer(room.classroom, viewer, actor, requests),
    [room.classroom, viewer, actor, requests],
  )

  const capsuleControl = useDialogControl()
  const flashbookControl = useDialogControl()
  const moduleControl = useDialogControl()

  const postsRef = useRef<SectionRef>(null)
  const capsulesRef = useRef<SectionRef>(null)
  const modulesRef = useRef<SectionRef>(null)
  const flashbooksRef = useRef<SectionRef>(null)

  useSetTitle(classroom.name)

  const addContent = useAddClassroomContentMutation(classroom.id)
  const removeContent = useRemoveClassroomContentMutation(classroom.id)

  const onRemoveContent = useCallback(
    (contentId: string) => {
      removeContent.mutate(
        {contentId},
        {onError: error => showClassroomError(i18n, error)},
      )
    },
    [i18n, removeContent],
  )

  /**
   * Writing a post for a classroom is writing a post.
   *
   * The composer is the app's own, so what comes out of it is a real Taderok
   * post with a real address — the one people comment on and react to — and
   * the classroom simply remembers that it belongs to this room. Nothing is
   * copied, and there is no separate kind of post to write here.
   *
   * The room goes with the composer, so the record says which room the words
   * were written in (`readClassroomPostMark`) rather than leaving the post to
   * look like its author's own: on a public room's page the post can be in the
   * feeds, drawn as the room's, and a private room's post is kept for the room.
   */
  const classroomRef = useMemo(
    () => ({
      id: classroom.id,
      name: classroom.name,
      handle: classroom.handle,
      ownerHandle: classroom.ownerHandle,
      ownerDid: classroom.ownerDid,
      visibility: classroom.visibility,
    }),
    [
      classroom.handle,
      classroom.id,
      classroom.name,
      classroom.ownerDid,
      classroom.ownerHandle,
      classroom.visibility,
    ],
  )

  const onPost = useCallback(
    (uri: string | undefined, choice?: ClassroomPostChoice) => {
      if (!uri) return
      /*
       * The entry's visibility is what the author chose in the audience pill,
       * and the seats narrow a room-only post. The server sets the author and
       * the room's level.
       */
      const visibility =
        choice?.visibility ??
        postVisibility(defaultPostAudience(classroom.visibility))
      addContent.mutate(
        {
          classroomId: classroom.id,
          kind: 'post',
          visibility,
          audience: visibility === 'members' ? choice?.audience : undefined,
          text: '',
          postUri: uri,
        },
        {
          onSuccess: () => Toast.show(l`Posted in ${classroom.name}`),
          onError: error => showClassroomError(i18n, error),
        },
      )
    },
    [addContent, classroom.id, classroom.name, classroom.visibility, i18n, l],
  )

  const onCompose = useCallback(() => {
    openComposer({
      logContext: 'Other',
      onPost,
      classroom: classroomRef,
    })
  }, [classroomRef, onPost, openComposer])

  const onScrollToTop = useCallback((index: number) => {
    if (index === 0) postsRef.current?.scrollToTop()
    else if (index === 1) capsulesRef.current?.scrollToTop()
    else if (index === 2) modulesRef.current?.scrollToTop()
    else flashbooksRef.current?.scrollToTop()
  }, [])

  return (
    <Layout.Screen testID="classroomScreen" style={[a.pt_0]}>
      <View style={styles.container}>
        <PagerWithHeader
          testID="classroomPager"
          isHeaderReady
          /*
           * Posts, then the three kinds of work a room can hold.
           *
           * A module sits between the capsules it is made of and the books, in the
           * order the app names that work everywhere else (see
           * `#/components/work/WorkKindSwitch`): a reader who has learnt the order on
           * one screen has learnt it on this one.
           */
          items={[l`Posts`, l`Capsules`, l`Modules`, l`Flashbooks`]}
          onPageSelected={() => {}}
          onCurrentPageSelected={onScrollToTop}
          allowHeaderOverScroll
          renderHeader={() => (
            <ClassroomHead
              classroom={classroom}
              viewer={viewer}
              inviteCode={inviteCode}
            />
          )}>
          {({headerHeight, isFocused, scrollElRef}) => (
            <ClassroomContentSection
              ref={postsRef}
              classroom={classroom}
              kind="post"
              canPost={canPost}
              headerHeight={headerHeight}
              isFocused={isFocused}
              scrollElRef={scrollElRef as ListRef}
              setScrollViewTag={() => {}}
              onPost={onPost}
              onAddCapsule={() => capsuleControl.open()}
              onAddFlashbook={() => flashbookControl.open()}
              onAddModule={() => moduleControl.open()}
              onRemove={onRemoveContent}
            />
          )}
          {({headerHeight, isFocused, scrollElRef}) => (
            <ClassroomContentSection
              ref={capsulesRef}
              classroom={classroom}
              kind="capsule"
              canPost={canPost}
              headerHeight={headerHeight}
              isFocused={isFocused}
              scrollElRef={scrollElRef as ListRef}
              setScrollViewTag={() => {}}
              onPost={onPost}
              onAddCapsule={() => capsuleControl.open()}
              onAddFlashbook={() => flashbookControl.open()}
              onAddModule={() => moduleControl.open()}
              onRemove={onRemoveContent}
            />
          )}
          {({headerHeight, isFocused, scrollElRef}) => (
            <ClassroomContentSection
              ref={modulesRef}
              classroom={classroom}
              kind="module"
              canPost={canPost}
              headerHeight={headerHeight}
              isFocused={isFocused}
              scrollElRef={scrollElRef as ListRef}
              setScrollViewTag={() => {}}
              onPost={onPost}
              onAddCapsule={() => capsuleControl.open()}
              onAddFlashbook={() => flashbookControl.open()}
              onAddModule={() => moduleControl.open()}
              onRemove={onRemoveContent}
            />
          )}
          {({headerHeight, isFocused, scrollElRef}) => (
            <ClassroomContentSection
              ref={flashbooksRef}
              classroom={classroom}
              kind="flashbook"
              canPost={canPost}
              headerHeight={headerHeight}
              isFocused={isFocused}
              scrollElRef={scrollElRef as ListRef}
              setScrollViewTag={() => {}}
              onPost={onPost}
              onAddCapsule={() => capsuleControl.open()}
              onAddFlashbook={() => flashbookControl.open()}
              onAddModule={() => moduleControl.open()}
              onRemove={onRemoveContent}
            />
          )}
        </PagerWithHeader>

        {canPost ? (
          <FAB
            testID="classroomComposeFAB"
            onPress={onCompose}
            icon={<EditBigIcon size="lg" fill={t.palette.white} />}
            accessibilityRole="button"
            accessibilityLabel={l`Write a post in this classroom`}
            accessibilityHint=""
          />
        ) : null}
      </View>

      <AddCapsuleDialog control={capsuleControl} classroom={classroom} />
      <AddFlashbookDialog control={flashbookControl} classroom={classroom} />
      <AddModuleDialog control={moduleControl} classroom={classroom} />
    </Layout.Screen>
  )
}

/**
 * Everything above the tabs, in the order a profile puts it in.
 *
 * A profile reads: cover, then the row of things you can do, then the name, the
 * handle, the counts, the bio, and the tabs. A classroom is read the same way,
 * in the same order, with the same sizes — a school's page should not teach a
 * reader a second layout for the same job. The one thing it has instead of an
 * avatar is nothing, and the action row simply runs to the right edge.
 *
 * The old panel that boxed the school's tools and said "You run this classroom"
 * is gone: the pill says who runs it (it offers *Edit classroom*), and the round
 * button beside it holds the rest, exactly as a profile does.
 */
function ClassroomHead({
  classroom,
  viewer,
  inviteCode,
}: {
  classroom: Classroom
  viewer: ClassroomViewer
  inviteCode?: string
}) {
  const t = useTheme()
  const {t: l} = useLingui()
  const actor = useClassroomActor()
  const isOwner = viewer.canManage
  const isMember = viewer.role !== null
  const pending = classroom.requests.length
  const grade = classroom.grade ? gradeLabel(classroom.grade) : null
  /*
   * An ask this room already said no to, still unanswered in the reader's own
   * head.
   *
   * A refusal used to be silent — the request left the room's list, the door came
   * back, and nothing said an answer had been given — which reads exactly like the
   * school never looking. So the room keeps the refusals it has given
   * (`Classroom.declined`) and this is the one place they are read: at the door, by
   * the person standing at it. Asking again clears it, because the room answered
   * the old question and not the next one.
   */
  const declined = !isMember
    ? (classroom.declined ?? []).find(d => d.did === actor?.did)
    : undefined
  const stillWaiting = classroom.requests.some(r => r.did === actor?.did)

  return (
    <View style={[t.atoms.bg]}>
      <ClassroomBanner cover={classroom.coverUrl ?? classroom.cover} />
      <View style={[a.px_lg, a.pt_md, a.pb_md, a.gap_sm]}>
        <View style={[a.flex_row, a.align_center, a.justify_end, a.pb_sm]}>
          <ClassroomHeaderActions
            classroom={classroom}
            viewer={viewer}
            inviteCode={inviteCode}
          />
        </View>

        <Text
          emoji
          testID="classroomDisplayName"
          style={[a.text_3xl, a.font_bold, a.leading_tight]}>
          {classroom.name}
        </Text>
        <Text
          testID="classroomHandle"
          style={[a.text_md, t.atoms.text_contrast_medium]}>
          {classroom.ownerName
            ? `${classroom.ownerName} · ${sanitizeHandle(classroom.ownerHandle, '@')}`
            : sanitizeHandle(classroom.ownerHandle, '@')}
        </Text>

        <View style={[a.flex_row, a.gap_xs, a.flex_wrap]}>
          <Tag>{l`Classroom`}</Tag>
          <Tag>
            {classroom.visibility === 'private' ? l`Private` : l`Public`}
          </Tag>
          {/*
            The level as the room says it: a school that called its room
            `Quran memorisation` is not introduced as `Other`
            (`classroomLevelName`), and a room that takes every country says so
            here, where a reader decides whether it is for them.
          */}
          {classroom.level ? <Tag>{classroomLevelName(classroom)}</Tag> : null}
          {grade ? <Tag>{l(grade)}</Tag> : null}
          {classroom.international ? <Tag>{l`International`}</Tag> : null}
          {classroom.levelNameType === 'SYSTEM' ? <Tag>{l`SYSTEM`}</Tag> : null}
        </View>

        <ClassroomMetrics classroom={classroom} viewer={viewer} />

        {/*
          A private classroom says so on its own page.

          It is the one thing a visitor who was handed the link has to be told: the
          room's work is deliberately not on show, and the code is how to get in.
          A public room needs no such line — everything a visitor may read is
          already there.
        */}
        {!isMember && classroom.visibility === 'private' ? (
          <View
            testID="classroomPrivateNotice"
            style={[a.rounded_md, a.p_md, t.atoms.bg_contrast_25]}>
            <Text style={[a.text_md, a.font_semi_bold]}>
              {l`This classroom is private`}
            </Text>
            <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
              {l`What its members write stays inside it. Join with the code you were given to read it.`}
            </Text>
          </View>
        ) : null}

        {/*
          What the room answered, said to the person who asked.

          It is not shown to somebody who is waiting on a *newer* ask: the table
          has been asked again, and a line about the room's last answer would be
          about a question that no longer exists.
        */}
        {declined && !stillWaiting ? (
          <Text
            testID="classroomRequestDeclined"
            style={[a.text_sm, {color: t.palette.negative_500}]}>
            {l`Your request to join was declined. You can ask again.`}
          </Text>
        ) : null}

        {classroom.description ? (
          <Text
            emoji
            selectable
            testID="classroomDescription"
            style={[a.text_md, a.leading_snug]}>
            {classroom.description}
          </Text>
        ) : null}

        {/*
          People are waiting on the school, so it is said out loud on the page
          rather than only living in the menu — the one thing a classroom has to
          nag about that a profile does not.
        */}
        {isOwner && pending > 0 ? (
          <Text
            testID="classroomPendingRequestsNotice"
            style={[a.text_sm, t.atoms.text_contrast_medium]}>
            {plural(pending, {
              one: '# person has asked to join.',
              other: '# people have asked to join.',
            })}
          </Text>
        ) : null}
      </View>
    </View>
  )
}

function Tag({children}: {children: React.ReactNode}) {
  const t = useTheme()
  return (
    <View
      style={[
        a.rounded_full,
        a.px_sm,
        {paddingVertical: 2},
        t.atoms.bg_contrast_25,
      ]}>
      <Text style={[a.text_xs, a.font_semi_bold, t.atoms.text_contrast_high]}>
        {children}
      </Text>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'column',
    height: '100%',
    // @ts-ignore Web-only.
    overflowAnchor: 'none',
  },
})
